
import { toast } from 'react-hot-toast';

declare var google: any;

let googleMapsPromise: Promise<void> | null = null;
let isGoogleMapsValid = false;
let currentApiKey = '';

type VoidCallback = () => void;
const authFailureListeners: Set<VoidCallback> = new Set();

const CALLBACK_NAME = 'googleMapsInitCallback';

export const onGoogleMapsAuthFailure = (callback: VoidCallback) => {
    authFailureListeners.add(callback);
    return () => { authFailureListeners.delete(callback); };
};

export const loadGoogleMaps = (apiKeyOverride?: string): Promise<void> => {
  // Se mudou a chave e a anterior falhou, reseta o ciclo para tentar novamente
  if (apiKeyOverride && apiKeyOverride !== currentApiKey && !isGoogleMapsValid) {
      googleMapsPromise = null;
  }

  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise(async (resolve, reject) => {
    // Verificação rigorosa: google.maps existe E geometry.spherical existe?
    const checkFullAvailability = () => {
        return typeof window !== 'undefined' && 
               (window as any).google && 
               (window as any).google.maps && 
               (window as any).google.maps.geometry && 
               (window as any).google.maps.geometry.spherical;
    };

    if (checkFullAvailability()) {
      isGoogleMapsValid = true;
      resolve();
      return;
    }

    let apiKey = apiKeyOverride || '';
    if (!apiKey) {
        try { apiKey = process.env.GOOGLE_MAPS_API_KEY || ''; } catch (e) {}
    }

    if (!apiKey || apiKey === 'undefined' || apiKey === 'YOUR_API_KEY') {
      isGoogleMapsValid = false;
      resolve();
      return;
    }

    currentApiKey = apiKey;

    // Se o script já existe mas a library não (talvez carregado por outra lib ou erro de rede)
    const existingScript = document.getElementById('google-maps-script');
    if (existingScript) {
        // Polling curto para ver se a lib aparece (scripts asíncronos podem demorar a atachar a lib ao namespace)
        for (let i = 0; i < 10; i++) {
            await new Promise(r => setTimeout(r, 200));
            if (checkFullAvailability()) {
                isGoogleMapsValid = true;
                resolve();
                return;
            }
        }
        // Se após 2s ainda não tem geometry, o script pode ter sido carregado sem as libraries corretas
        // Removemos para forçar o reload correto
        existingScript.remove();
        if ((window as any).google) delete (window as any).google;
    }

    (window as any).gm_authFailure = () => {
      console.error("Google Maps Auth Failed.");
      isGoogleMapsValid = false;
      googleMapsPromise = null;
      authFailureListeners.forEach(cb => cb());
      toast.error("Chave do Google Maps recusada.", { id: 'maps-auth-err' });
    };

    (window as any)[CALLBACK_NAME] = () => {
        // Mesmo no callback, garantimos que a lib geometry está lá
        if (checkFullAvailability()) {
            isGoogleMapsValid = true;
            resolve();
        } else {
            // Se o callback disparou mas geometry não está lá, esperamos um pouco
            setTimeout(() => {
                isGoogleMapsValid = checkFullAvailability();
                resolve();
            }, 500);
        }
    };

    const script = document.createElement('script');
    script.id = 'google-maps-script';
    // CRITICAL: Adicionado 'marker' nas libraries para suporte ao AdvancedMarkerElement
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry,places,visualization,marker&loading=async&callback=${CALLBACK_NAME}`;
    script.async = true;
    script.defer = true;
    
    script.onerror = () => {
        isGoogleMapsValid = false;
        googleMapsPromise = null;
        resolve();
    };
    
    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

export const checkGoogleMapsAvailability = () => {
    // check rigoroso para os elementos que o motor tático consome
    return isGoogleMapsValid && 
           typeof google !== 'undefined' && 
           typeof google.maps !== 'undefined' &&
           typeof google.maps.Map === 'function' &&
           google.maps.geometry && 
           google.maps.geometry.spherical;
};
